import { useAtomValue } from 'jotai';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { usePlatform } from '@localagent/platform';
import { TitleBar } from './TitleBar';
import { LeftSidebar } from './LeftSidebar';
import { MainContent } from './MainContent';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { useNotificationStream } from '../../hooks/useNotificationStream';
import { useLiveFeedHandle } from '../../hooks/useLiveFeedHandle';
import { useOpenQuestionsHandle } from '../../hooks/useOpenQuestionsHandle';
import { useCacheReconnectInvalidation } from '../../hooks/useCacheReconnectInvalidation';
import { NotificationToastDispatcher } from '../notifications/notificationToastDispatcher';
import { DaemonNoticeDispatcher } from '../daemon/DaemonNoticeDispatcher';
import { useReconcileLaunchedWorkspaces } from '../../hooks/useWorkspaces';
import { useCatalogSubscription } from '../../hooks/useCatalogSubscription';
import {
  useShogunDock,
  useShogunRoutePlacement,
  type ShogunDockMode,
} from '../shogun/useShogunDock';
import { ShogunPanel } from '../shogun/ShogunPanel';
import { useShogunConfirm, ShogunConfirmDialog } from '../shogun/ShogunConfirmDialog';
import { useExperienceMode } from '../../hooks/useExperienceMode';
import { useWebBridgeClient } from '../../hooks/useWebBridgeClient';
import { useOnboardingStatus } from '../../hooks/useBilling';
import { DaemonSessionGate } from '../../auth/DaemonSessionGate';

// Docked edge → shell flex-direction. The panel is ALWAYS the second child of
// the shell container (below), so which edge it occupies is purely a matter of
// direction: `row` → right, `row-reverse` → left, `col` → bottom, `col-reverse`
// → top. Floating/closed fall back to `flex-row` (the panel is a fixed overlay
// or absent, so it takes no lane). Identical child types at identical positions
// across every mode ⇒ React reconciles in place ⇒ the chat never remounts on
// a dock change.
const DOCK_DIRECTION: Record<ShogunDockMode, string> = {
  right: 'flex-row',
  left: 'flex-row-reverse',
  bottom: 'flex-col',
  top: 'flex-col-reverse',
  floating: 'flex-row',
};

export function MainLayout() {
  useKeyboardShortcuts();
  // ONE global bridge-demux subscriber: streams BOTH errors + notifications
  // into their stores app-wide, on ANY view — replaces the deleted 5s
  // /v1/errors/list poller (useErrorEvents/ErrorWSManager).
  useNotificationStream();
  useLiveFeedHandle();
  useOpenQuestionsHandle();
  useCacheReconnectInvalidation();

  // Web bridge client + destructive-confirm: always mounted when authenticated
  // (beyond chat / Shogun panel open). Opens the per-tab BridgeEventStream,
  // registers the platform_call executor, and hosts the confirm dialog so
  // agent-initiated actions work with the panel closed.
  // One pass, mounted once: prunes launched ids whose workspace is gone.
  useReconcileLaunchedWorkspaces();
  useCatalogSubscription('sessions');
  const { confirm, pending, approve, deny } = useShogunConfirm();
  const { mode } = useExperienceMode();
  useWebBridgeClient(confirm);

  const platform = usePlatform();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);

  // Agent-mediated routes supply placement (app-shell ADR-016). The panel
  // mounts only when that route force-opens it — not via a user launcher.
  const shogunDock = useShogunDock(useShogunRoutePlacement());

  const shellDirection = shogunDock.open ? DOCK_DIRECTION[shogunDock.mode] : 'flex-row';

  // Onboarding gate — redirect to /onboarding if authenticated but onboarding
  // not complete.
  const location = useLocation();
  const { data: onboardingStatus, isLoading: onboardingLoading } = useOnboardingStatus();
  const needsOnboarding =
    isAuthenticated &&
    !onboardingLoading &&
    onboardingStatus &&
    onboardingStatus.phase !== 'complete';

  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="flex h-full w-full flex-col" data-testid="app-ready">
      {/*
        Desktop auth is a SIBLING of the routed shell, never its parent.
        RouterProvider stays mounted for the window lifetime; the gate mirrors
        daemon `/events` and projects non-usable phases as an overlay.
      */}
      {platform.type === 'desktop' ? <DaemonSessionGate /> : null}
      <TitleBar />
      {/* Nav rail stays outside the dock reflow (app-shell ADR-016). */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <LeftSidebar />
        <div className={`flex min-h-0 min-w-0 flex-1 overflow-hidden ${shellDirection}`}>
          {/* Routed content. Always the FIRST child of the dock container so it
              flexes to fill whatever the docked panel leaves. */}
          <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
            <MainContent>
              <Outlet />
            </MainContent>
          </div>
          {/* Shogun assistant panel — ALWAYS the second child (stable order), so a
              dock change is pure flex-direction and never remounts the chat
              Docked ⇒ in-flow lane; floating ⇒ fixed overlay; closed
              ⇒ null (no lane). Bridge + confirm live above on MainLayout. */}
          <ShogunPanel dock={shogunDock} />
        </div>
      </div>
      {/* Destructive platform_call confirm — always mounted (panel may be closed). */}
      <ShogunConfirmDialog pending={pending} onApprove={approve} onDeny={deny} mode={mode} />
      {/* App-wide notification/error toast dispatcher — raises a toast on a new
          push frame from ANY view, honoring display-time mute. */}
      <NotificationToastDispatcher />
      {/* Daemon-originated notices — a SEPARATE channel from the one above,
          which is backend-originated over the cloud bridge (shogunchop ADR-d2bfe). */}
      <DaemonNoticeDispatcher />
    </div>
  );
}
